import React, { Fragment, useEffect } from 'react';
import { DashboardSkeleton } from 'pages/ReportsPage/components/AnalyticsDashboards/Skeleton';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';
import { useAgencyDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/store/agencyDashboard.store';
import { useAgencyKpiCards } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/useAgencyKpiCards';
import { KpiStatCardRow } from 'pages/ReportsPage/components/AnalyticsDashboards/KpiStatCardRow';
import { WidgetCardWrapper } from 'pages/ReportsPage/components/AnalyticsDashboards/WidgetCardWrapper';
import { AgencyShiftsByPositionChart } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/AgencyShiftsByPositionChart';
import { AgencyHoursDistributionChart } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/AgencyHoursDistributionChart';
import { AgencyHoursByProviderChart } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/AgencyHoursByProviderChart';
import { LeadTimeBeforeShiftStartChart } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/LeadTimeBeforeShiftStartChart';
import { HardestTimeframesChart } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/HardestTimeframesChart';
import { ShiftsSentBySchedulerTable } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard/ShiftsSentBySchedulerTable';
import {
  DashboardContainer,
  DashboardHeader,
  DashboardContent,
  DashboardTitle,
  FullWidthSection,
  WidgetGrid,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';
import { FilterBar } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar';

export const AgencyDashboard: React.FC = () => {
  const fetchData = useAgencyDashboardStore((s) => s.fetchData);
  const reset = useAgencyDashboardStore((s) => s.reset);
  const isLoading = useAgencyDashboardStore((s) => s.isLoading);
  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const kpiCards = useAgencyKpiCards();
  const showSkeleton = isLoading;

  useEffect(() => {
    return fetchData(dateRange, locationIds, positions, employmentTypes);
  }, [fetchData, dateRange, locationIds, employmentTypes, positions]);

  useEffect(() => {
    return () => reset();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardContainer aria-label="Agency Dashboard">
      <DashboardHeader>
        <DashboardTitle>Agency Dashboard</DashboardTitle>
      </DashboardHeader>
      <FilterBar />
      <DashboardContent>
        {showSkeleton ? (
          <DashboardSkeleton kpiCount={3} widgetCount={6} />
        ) : (
          <Fragment>
            <div aria-live="polite">
              <KpiStatCardRow cards={kpiCards} fullWidth />
            </div>
            <WidgetGrid>
              <WidgetCardWrapper
                title="Agency Hours by Provider"
                subtitle="Hours filled per external agency"
              >
                <AgencyHoursByProviderChart />
              </WidgetCardWrapper>
              <WidgetCardWrapper
                title="Agency shifts by position"
                subtitle="Agency shifts and hours grouped by position"
              >
                <AgencyShiftsByPositionChart />
              </WidgetCardWrapper>
            </WidgetGrid>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Lead Time Before Shift Start When Agency Requested"
                subtitle="How early the agency shift was created before it starts — red flags last-minute requests"
              >
                <LeadTimeBeforeShiftStartChart />
              </WidgetCardWrapper>
            </FullWidthSection>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Agency Hours Distribution"
                subtitle="Daily view — agency hours (bar) and sent to agency rate (%)"
              >
                <AgencyHoursDistributionChart />
              </WidgetCardWrapper>
            </FullWidthSection>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Hardest Timeframes to Fill Internally"
                subtitle="Agency shifts by day and time of day; darker cells are slots most often covered by agency"
              >
                <HardestTimeframesChart />
              </WidgetCardWrapper>
            </FullWidthSection>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Shifts Sent to Agency by Scheduler"
                subtitle="Agency shifts by scheduler and location, sorted by volume"
              >
                <ShiftsSentBySchedulerTable />
              </WidgetCardWrapper>
            </FullWidthSection>
          </Fragment>
        )}
      </DashboardContent>
    </DashboardContainer>
  );
};
